import { useState } from 'react';
import { useParams, Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  Buildings,
  ChartLine,
  ShieldCheck,
  CurrencyCircleDollar,
  CalendarBlank,
  Users,
} from '@phosphor-icons/react';

import Page from '@/components/page/Page';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import {
  getCustomerById,
  getCommunicationsForCustomer,
  getScheduledCommunicationsForCustomer,
  getAIAgentRunsForCustomer,
  getAIAgentMonitorsForCustomer,
  MOCK_CUSTOMERS,
  type ARCommunication,
} from '../data/mockARData';
import { EmailDetailModal } from './components/EmailDetailModal';
import { PhoneCallDetailModal } from './components/PhoneCallDetailModal';
import { CommunicationsTimeline } from './components/CommunicationsTimeline';
import { AIAgentActivityPanel } from './components/AIAgentActivityPanel';
import { VideoRecordingModal } from './components/VideoRecordingModal';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const STATUS_CONFIG = {
  needs_attention: {
    label: 'Needs Attention',
    variant: 'warning' as const,
  },
  in_progress: {
    label: 'In Progress',
    variant: 'secondary' as const,
  },
  on_track: {
    label: 'On Track',
    variant: 'outline' as const,
  },
};

// Toggle to show/hide aging details in the header card
const SHOW_AGING_IN_HEADER = false;

export function CustomerCollectionsPage() {
  const { customerId } = useParams({
    from: '/authenticated/ar/collections/$customerId',
  });

  const customer = getCustomerById(customerId) || MOCK_CUSTOMERS[0];
  const communications = getCommunicationsForCustomer(customer.id);
  const scheduledCommunications = getScheduledCommunicationsForCustomer(
    customer.id,
  );
  const agentRuns = getAIAgentRunsForCustomer(customer.id);
  const agentMonitors = getAIAgentMonitorsForCustomer(customer.id);

  const [selectedCommunication, setSelectedCommunication] =
    useState<ARCommunication | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);

  const handleWatchRecording = (videoUrl: string) => {
    setSelectedVideoUrl(videoUrl);
    setVideoModalOpen(true);
  };

  const handleCommunicationClick = (comm: ARCommunication) => {
    setSelectedCommunication(comm);
    if (comm.type === 'email') {
      setEmailModalOpen(true);
    } else {
      setPhoneModalOpen(true);
    }
  };

  const statusConfig = STATUS_CONFIG[customer.status];

  return (
    <Page>
      <div className="space-y-6">
        {/* Back Button */}
        <Link
          to="/ar/collections"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Collections
        </Link>

        {/* Customer Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold">{customer.name}</h1>
                  <Badge variant={statusConfig.variant}>
                    {statusConfig.label}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>Account: {customer.accountNumber}</span>
                  <span className="flex items-center gap-1">
                    <Buildings className="h-3.5 w-3.5" />
                    D&B: {customer.dunsNumber}
                  </span>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-5 gap-6 mt-6 pt-6 border-t">
              <div>
                <div className="text-sm text-muted-foreground">
                  Total Outstanding
                </div>
                <div className="text-xl font-bold">
                  {formatCurrency(customer.totalOutstanding)}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">
                  Overdue Balance
                </div>
                <div className="text-xl font-bold">
                  {formatCurrency(customer.pastDueAmount)}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">
                  Open Invoices
                </div>
                <div className="text-xl font-bold">
                  {customer.openInvoicesCount}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">
                  Payment Terms
                </div>
                <div className="text-xl font-bold">{customer.paymentTerms}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Contact</div>
                <div className="text-sm font-medium">
                  {customer.contactPerson}
                </div>
                <div className="text-xs text-muted-foreground">
                  {customer.email}
                </div>
              </div>
            </div>

            {/* Aging Details */}
            {SHOW_AGING_IN_HEADER && (
              <div className="grid grid-cols-5 gap-6 mt-6 pt-6 border-t">
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">
                    Current
                  </div>
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(customer.aging.current)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">
                    1-30 Days
                  </div>
                  <div className="text-lg font-semibold text-lime-600">
                    {formatCurrency(customer.aging.days30)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">
                    31-60 Days
                  </div>
                  <div className="text-lg font-semibold text-yellow-600">
                    {formatCurrency(customer.aging.days60)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">
                    61-90 Days
                  </div>
                  <div className="text-lg font-semibold text-orange-600">
                    {formatCurrency(customer.aging.days90)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">
                    90+ Days
                  </div>
                  <div className="text-lg font-semibold text-red-600">
                    {formatCurrency(customer.aging.over90)}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Column - 60% */}
          <div className="lg:col-span-3 space-y-6">
            {/* Communications Timeline with Tabs */}
            <CommunicationsTimeline
              communications={communications}
              scheduledCommunications={scheduledCommunications}
              onViewDetails={handleCommunicationClick}
            />
          </div>

          {/* Right Column - 40% */}
          <div className="lg:col-span-2 space-y-6">
            {/* D&B Business Credit Profile */}
            {customer.dnbProfile && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <Buildings className="h-4 w-4" />
                    D&B Business Credit Profile
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Credit Scores Row */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-primary">
                        {customer.dnbProfile.rating}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        D&B Rating
                      </div>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <div
                        className={`text-2xl font-bold ${
                          customer.dnbProfile.paydexScore >= 80
                            ? 'text-green-600'
                            : customer.dnbProfile.paydexScore >= 70
                              ? 'text-yellow-600'
                              : 'text-red-600'
                        }`}
                      >
                        {customer.dnbProfile.paydexScore}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        PAYDEX Score
                      </div>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <Badge
                        variant={
                          customer.dnbProfile.failureRiskClass === 'Low'
                            ? 'outline'
                            : customer.dnbProfile.failureRiskClass ===
                                'Moderate'
                              ? 'warning'
                              : 'destructive'
                        }
                        className="text-sm"
                      >
                        {customer.dnbProfile.failureRiskClass} Risk
                      </Badge>
                      <div className="text-xs text-muted-foreground mt-1">
                        Failure Risk
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between py-1.5 border-b">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <CurrencyCircleDollar className="h-4 w-4" />
                        Credit Limit Recommendation
                      </span>
                      <span className="font-medium">
                        {formatCurrency(
                          customer.dnbProfile.creditLimitRecommendation,
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <ChartLine className="h-4 w-4" />
                        Annual Revenue
                      </span>
                      <span className="font-medium">
                        {formatCurrency(customer.dnbProfile.annualRevenue)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Employees
                      </span>
                      <span className="font-medium">
                        {customer.dnbProfile.employees.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <CalendarBlank className="h-4 w-4" />
                        Years in Business
                      </span>
                      <span className="font-medium">
                        {customer.dnbProfile.yearsInBusiness}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1.5">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4" />
                        Delinquency Score
                      </span>
                      <span className="font-medium">
                        {customer.dnbProfile.delinquencyScore}/9
                      </span>
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground text-right pt-2 border-t">
                    Last updated: {formatDate(customer.dnbProfile.lastUpdated)}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* AI Agent Activity Panel (only shows if there's activity) */}
            <AIAgentActivityPanel
              runs={agentRuns}
              monitors={agentMonitors}
              onWatchRecording={handleWatchRecording}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      <EmailDetailModal
        communication={
          selectedCommunication?.type === 'email' ? selectedCommunication : null
        }
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
      />
      <PhoneCallDetailModal
        communication={
          selectedCommunication?.type === 'phone' ? selectedCommunication : null
        }
        open={phoneModalOpen}
        onOpenChange={setPhoneModalOpen}
      />
      <VideoRecordingModal
        videoUrl={selectedVideoUrl}
        open={videoModalOpen}
        onOpenChange={setVideoModalOpen}
      />
    </Page>
  );
}

export default CustomerCollectionsPage;
